import {
    getSurveysOptions,
    QuestionsForManagerQueryOptions,
    getSurveyOptions,
} from "@pulse/api/queryApi";
import {
    createQuestion,
    deleteQuestion,
    reorderQuestions,
    updateQuestion,
    updateSurvey,
    type CreateQuestion,
    type PartialCreateQuestion,
    type Question,
    type Survey,
} from "@pulse/api/surveyApi";
import { useCurrentQuestions } from "@pulse/hooks/surveyHooks";
import { queryClient } from "@pulse/queryClient";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSections } from "../Sections/hooks";

function useCreateQuestion(
    surveyId: number,
    options?: UseMutationOptions<Question, Error, CreateQuestion, unknown>,
) {
    return useMutation({
        ...options,
        mutationKey: ["createQuestion", surveyId],
        mutationFn: (data: CreateQuestion) => createQuestion({ surveyId, data }),
        onSettled: () =>
            queryClient.invalidateQueries(QuestionsForManagerQueryOptions({ surveyId })),
    });
}

function useRemoveQuestion(questionId: number) {
    return useMutation({
        mutationKey: ["deleteQuestion", questionId],
        mutationFn: () => deleteQuestion({ questionId }),
        onSuccess: (data) =>
            queryClient.invalidateQueries(
                QuestionsForManagerQueryOptions({ surveyId: data.surveyId }),
            ),
    });
}

function useUpdateQuestion(questionId: number) {
    return useMutation({
        mutationKey: ["updateQuestion", questionId],
        mutationFn: (data: Partial<CreateQuestion>) => updateQuestion({ questionId, data }),
        onSuccess: (data) =>
            queryClient.invalidateQueries(
                QuestionsForManagerQueryOptions({ surveyId: data.surveyId }),
            ),
    });
}

function useReorderQuestions(surveyId: number) {
    const queryOptions = QuestionsForManagerQueryOptions({ surveyId });

    return useMutation({
        mutationKey: ["reorderQuestions", surveyId],
        mutationFn: (questionIds: number[]) => reorderQuestions({ surveyId, questionIds }),
        onMutate: async (questionIds) => {
            await queryClient.cancelQueries(queryOptions);

            const previousQuestions = queryClient.getQueryData<Question[]>(queryOptions.queryKey);
            queryClient.setQueryData<Question[]>(queryOptions.queryKey, (currentQuestions) => {
                if (!currentQuestions) {
                    return currentQuestions;
                }

                const questionsById = new Map(
                    currentQuestions.map((question) => [question.id, question]),
                );

                return questionIds
                    .map((questionId, index) => {
                        const question = questionsById.get(questionId);
                        if (!question) {
                            return null;
                        }

                        return {
                            ...question,
                            questionOrder: index,
                        };
                    })
                    .filter((question): question is Question => question !== null);
            });

            return { previousQuestions };
        },
        onError: (_error, _questionIds, context) => {
            if (context?.previousQuestions) {
                queryClient.setQueryData(queryOptions.queryKey, context.previousQuestions);
            }
        },
        onSuccess: (questions) => {
            queryClient.setQueryData(queryOptions.queryKey, questions);
        },
        onSettled: () => queryClient.invalidateQueries(queryOptions),
    });
}

function useUpdateSurvey(surveyId: number) {
    return useMutation({
        mutationFn: async (data: Partial<Survey>) => updateSurvey({ surveyId, data }),
        onSuccess: (updatedSurvey) => {
            // Invalidate the individual survey cache
            queryClient.invalidateQueries(getSurveyOptions({ surveyId }));
            // Invalidate the surveys list cache to update the title in the list
            queryClient.invalidateQueries(
                getSurveysOptions({ pulseInstanceId: updatedSurvey.pulseInstanceId }),
            );
        },
    });
}

function useCreateSurvey() {
    const surveyId = useParams({
        from: "/_hub/$customerId/pulse/$pulseId/surveys/$surveyId/edit",
    }).surveyId;

    const { setActive: setActiveSection } = useSections();
    const { t } = useTranslation();
    const { data: questions } = useCurrentQuestions();
    const surveySavedNotification = useSurveySavedNotification();

    const createQuestionMutation = useCreateQuestion(+surveyId, {
        onSuccess: (data) => {
            setActiveSection(data.id);
        },
    });

    const highestQuestionOrder = useMemo(() => {
        return (
            questions?.reduce((max, question) => Math.max(max, question.questionOrder), -1) ?? -1
        );
    }, [questions]);

    const createQuestion = async (question: PartialCreateQuestion): Promise<Question> => {
        const baseQuestion = {
            questionText: t("question.defaultQuestionText"),
            questionOrder: highestQuestionOrder + 1,
            questionOptions: null,
            optional: false,
        };

        const promise = createQuestionMutation.mutateAsync({ ...baseQuestion, ...question });

        surveySavedNotification(promise);
        return promise;
    };

    const isPending = createQuestionMutation.isPending;

    return {
        createQuestion,
        highestQuestionOrder,
        questions,
        isPending,
    };
}

export const useSurveySavedNotification = () => {
    const { t } = useTranslation();

    return (promise: Promise<unknown>) =>
        toast.promise(promise, {
            loading: t("survey.saving"),
            success: t("survey.saved"),
            classNames: { icon: "text-chameleon-green" },
        });
};

export {
    useCreateQuestion,
    useRemoveQuestion,
    useUpdateQuestion,
    useReorderQuestions,
    useCreateSurvey,
    useUpdateSurvey,
};
